'use client'

import EmptyFlightScreenWrapper from '@/src/components/common/srp/emptyFlightScreen'
import SrpErrorStateWrapper from '@/src/components/common/srp/errorState'

import HeaderContainer from '@/src/components/common/header/HeaderRouter'
import LoaderImage from '@/src/components/common/loaderImage'
import FiltersWrapper from '@/src/components/common/srp/filters'
import AirlineFilterWrapperTopBar from '@/src/components/common/srp/filters/airlineFilterTopBar'
import StillAroundPopup from '@/src/components/common/srp/stillAroundPopup/StillAroundPopup'
import Statusbar from '@/src/components/common/ui/statusbar'
import CouponSectionWrapper from '@/src/components/desktop/srp/couponSection'
import FareFamilyWrapper from '@/src/components/desktop/srp/fareFamily'
import FlightResultsWrapper from '@/src/components/desktop/srp/flightResults'
import FlightDetailsWrapperDesktop from '@/src/components/desktop/srp/flightResults/flightDetails'
import FlightDetailsSlider from '@/src/components/desktop/srp/flightResults/flightDetails/flightDetails'
import DesktopSrpLoadingShimmer from '@/src/components/desktop/srp/loadingShimmers/DesktopSrpLoadingShimmer'
import RTSolutionFlowWrapper from '@/src/components/desktop/srp/rtSolutionViewFlow'
import SearchHeader from '@/src/components/desktop/srp/searchHeader'
import SortTabsDesktop from '@/src/components/desktop/srp/sortTabs'
import { DESKTOP } from '@/src/constants/deviceTypes'
import ErrorBoundaryWrapper from '@/src/containers/common/ErrorBoundary'
import useClevertapForSrp from '@/src/hooks/analytics/clevertap/srp'
import useSrpController from '@/src/hooks/srp/srpController'
import useSearchResults from '@/src/hooks/srp/useSearchResults'
import { useSetLanguageCookie } from '@/src/utils/language'
import { Backdrop, Box, Container, Dialog } from '@mui/material'
import { useLocale } from 'next-intl'
import { useEffect, useState } from 'react'
import { Toaster } from 'react-hot-toast'
import StickyBox from 'react-sticky-box'
import NearbyAirportPopup from '@/src/components/common/srp/nearbyAirportPopup'
import useNearByAirportAlertManager from '@/src/hooks/srp/nearbyAirportAlertManager'
import AirspaceAlertWrapper from '@/src/components/common/home/common/airspaceAlert'

interface SrpContainerDesktopProps {
    domainCountry?: string
}

const SrpContainerDesktop = ({ domainCountry }: SrpContainerDesktopProps = {}) => {
    // Set selectedLanguage cookie on page load based on locale
    useSetLanguageCookie()

    const {
        selectedFlightForFareFamily,
        selectedFlightForFlightDetails,
        handleCreateBooking,
        handleFlightDetailsClose,
        onViewFlightDetails,
        setSelectedFlightForFareFamily,
        handleBookClick,
        rawFareFamilyData,
        setRawFareFamilyData,
        isBookingInProgress,
        loadingFlightKey,
        showStillAroundPopup,
        handleReFetchWithSameQuery,
        handleCloseFareFamily,
    } = useSrpController({
        deviceType: 'DESKTOP'
    })

    const {
        srpResultsLoading,
        srpResultsError,
        invalidUrl,
        showEmptyFlightScreen,
        jsons,
        sectors,
        sid,
        isRT,
        isSolutionView
    } = useSearchResults()

    // Nearby airport alert manager - handles all nearby airport logic internally
    const {
        handleFlightSelection,
        handleNearbyPopupContinue,
        handleNearbyPopupClose,
        isOpen,
        popupData
    } = useNearByAirportAlertManager({
        onContinue: handleBookClick,
        jsons : jsons!!,
        sectors : sectors!!,
        handleFlightDetailsClose
    })

    const { triggerSrpEvent } = useClevertapForSrp()
    const [menuOpen, setMenuOpen] = useState(false)

    const handleOverlayOpen = () => setMenuOpen(true)
    const handleOverlayClose = () => setMenuOpen(false)
    const locale = useLocale()
    useEffect(() => {
        if (srpResultsError) {
            triggerSrpEvent('Air_Failures', {
                action_name: 'srp_search_failure',
                action_type: 'API',
                action_value: `Error ${srpResultsError.status}: 'Failed to fetch search results'`
            })
        }
        if (invalidUrl) {
            triggerSrpEvent('Air_Failures', {
                action_name: invalidUrl === 'dateInPast' ? 'past_date_error' : 'invalid_url',
                action_type: 'VALIDATION',
                action_value: 'Selected date is in the past'
            })
        }
    }, [srpResultsError, invalidUrl, triggerSrpEvent])

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', position: 'relative' }}>
            <LoaderImage height={50} width={600} />
            <Toaster position="bottom-right" reverseOrder={false} gutter={8} />
            <ErrorBoundaryWrapper identifier="HeaderContainer-desktop-srp">
                <HeaderContainer domainCountry={domainCountry} />
            </ErrorBoundaryWrapper>
            <ErrorBoundaryWrapper identifier="Statusbar-desktop-srp">
                <Statusbar loading={srpResultsLoading} />
            </ErrorBoundaryWrapper>
            <Backdrop
                open={menuOpen}
                onClick={handleOverlayClose}
                sx={{
                    zIndex: 1300,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)'
                }}
            />
            <AirspaceAlertWrapper />
            <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, lg: 4 }, py: 1 }}>
                {invalidUrl !== 'dateInPast' && (
                    <Box
                        sx={theme => ({
                            width: {
                                xs: `calc(100% + ${theme.spacing(3)})`,
                                lg: `calc(100% + ${theme.spacing(4)})`
                            },
                            position: 'relative',
                            zIndex: menuOpen ? 1400 : 1,
                            pb: 1
                        })}
                    >
                        <ErrorBoundaryWrapper identifier="searchHeader-desktop-srp">
                            <SearchHeader
                                jsons={jsons}
                                overlayOpen={handleOverlayOpen}
                                overlayClose={handleOverlayClose}
                                srpResultsLoading={srpResultsLoading}
                            />
                        </ErrorBoundaryWrapper>
                    </Box>
                )}

                {!!srpResultsError || invalidUrl ? (
                    <ErrorBoundaryWrapper identifier="SrpErrorStateWrapper-desktop-srp">
                        <SrpErrorStateWrapper
                            pastDateError={invalidUrl === 'dateInPast'}
                            errorStatus={invalidUrl === 'requiredFieldsMissing' ? 400 : srpResultsError?.status || 500}
                        />
                    </ErrorBoundaryWrapper>
                ) : srpResultsLoading ? (
                    <DesktopSrpLoadingShimmer />
                ) : (
                    <>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '280px 1fr',
                                gap: { xs: 3, lg: 4 },
                                alignItems: 'start'
                            }}
                        >
                            <StickyBox offsetTop={24} offsetBottom={24}>
                                <ErrorBoundaryWrapper identifier="sideBar-filters-wrapper-desktop-srp">
                                    <FiltersWrapper deviceType={DESKTOP} />
                                </ErrorBoundaryWrapper>
                            </StickyBox>

                            <Box sx={{ order: { xs: 1, lg: 2 } }}>
                                <CouponSectionWrapper jsons={jsons} isRT={isRT} />

                                <>
                                    {isRT && isSolutionView ? (
                                        <ErrorBoundaryWrapper identifier="RTSolutionFlowWrapper-desktop-srp">
                                            <RTSolutionFlowWrapper handleFlightBook={handleCreateBooking} />
                                        </ErrorBoundaryWrapper>
                                    ) : (
                                        <>
                                            {showEmptyFlightScreen ? (
                                                <ErrorBoundaryWrapper identifier="EmptyFlightScreenWrapper-desktop-srp">
                                                    <EmptyFlightScreenWrapper deviceType={DESKTOP} />
                                                </ErrorBoundaryWrapper>
                                            ) : (
                                                <>
                                                    <ErrorBoundaryWrapper identifier="SortTabWrapper-desktop-srp">
                                                        <SortTabsDesktop />
                                                    </ErrorBoundaryWrapper>
                                                    <ErrorBoundaryWrapper identifier="AirlineFilterWrapperTopBar-desktop-srp">
                                                        <AirlineFilterWrapperTopBar deviceType={DESKTOP} />
                                                    </ErrorBoundaryWrapper>
                                                    <ErrorBoundaryWrapper identifier="FlightResultsWrapper-desktop-srp">
                                                        <FlightResultsWrapper
                                                            onBookFlight={handleFlightSelection}
                                                            onSortByPrice={() => { }}
                                                            onViewDetails={flight => onViewFlightDetails([flight])}
                                                            isBookingInProgress={isBookingInProgress}
                                                            loadingFlightKey={loadingFlightKey}
                                                        />
                                                    </ErrorBoundaryWrapper>
                                                </>
                                            )}
                                        </>
                                    )}
                                </>
                            </Box>
                        </Box>

                        {/* Flight Details Slider  for one way*/}

                        <FlightDetailsWrapperDesktop
                            open={!!selectedFlightForFlightDetails}
                            onClose={handleFlightDetailsClose}
                        >
                            {!!selectedFlightForFlightDetails ? (
                                <FlightDetailsSlider
                                    onClose={handleFlightDetailsClose}
                                    flights={selectedFlightForFlightDetails}
                                    jsons={jsons!}
                                    sectors={sectors!}
                                    onSelect={() => handleFlightSelection(selectedFlightForFlightDetails[0])}
                                    sid={sid!}
                                    showBaggage={true}
                                    isRTSolutionView={false}
                                    isBookingInProgress={isBookingInProgress}
                                />
                            ) : null}
                        </FlightDetailsWrapperDesktop>

                        <NearbyAirportPopup
                            open={isOpen}
                            onClose={handleNearbyPopupClose}
                            onContinue={handleNearbyPopupContinue}
                            data={popupData}
                        />

                        <Dialog
                            dir={locale === 'ar' ? 'rtl' : 'ltr'}
                            open={!!selectedFlightForFareFamily}
                            onClose={() => setSelectedFlightForFareFamily(null)}
                            maxWidth="xl"
                            fullWidth
                            slotProps={{
                                paper: { sx: { maxWidth: '80rem', margin: 0, minWidth: 'min(100%,1024px)' } },
                                backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.7)' } }
                            }}
                            keepMounted
                        >
                            <FareFamilyWrapper
                                flight={selectedFlightForFareFamily ?? undefined}
                                sectors={sectors!}
                                handleClose={handleCloseFareFamily}
                                handleCreateBooking={(flight, fareFamilyData) =>
                                    handleCreateBooking([flight], [fareFamilyData])
                                }
                                jsons={jsons!}
                                desktop={true}
                                rawFareFamilyData={rawFareFamilyData ? rawFareFamilyData : undefined}
                            />
                        </Dialog>
                    </>
                )}
            </Container>

            {showStillAroundPopup && <StillAroundPopup handleReFetchWithSameQuery={handleReFetchWithSameQuery} />}
        </Box>
    )
}

export default SrpContainerDesktop